import { segmentService } from '@/services/Segment'
import {
  SessionKey,
  sessionStorageService,
} from '@/services/SessionStorage/SessionStorageService'

/** Where the conversation is happening. */
export type AssistantSurface = 'panel' | 'page'

/** How a question reached the assistant. */
export type AssistantQuestionSource =
  | 'typed'
  | 'suggestion'
  | 'quick_reply'
  /** A follow-up chip on the nudge's briefing card. */
  | 'briefing'

/**
 * What opened the docked panel: the top-bar button (narrow screens), a
 * question typed in the top-bar field, a "Try asking" suggestion there,
 * Review on the nudge, or the top-bar button reopening a conversation
 * already in progress, at any width (`resume`: minimised, restored from
 * this tab's history after a reload, or a briefing card still held).
 * Missing on panel opens from before ENG-2165 (the corner launcher), so the
 * two can be compared.
 */
export type AssistantEntryPoint =
  | 'icon'
  | 'ask_input'
  | 'suggestion'
  | 'nudge_review'
  | 'resume'

/**
 * Ask AttorneyShare usage events, sent the same way as the other Segment
 * events in the app. Never carries what the user typed or what the
 * assistant said: only which surface, what opened the panel, how the
 * question arrived, which kind of change was approved or cancelled and which
 * kind of task a briefing was about.
 */
type AssistantEvent =
  | {
      event: 'assistant__opened'
      properties: {
        surface: AssistantSurface
        /** Panel only. */
        entry_point?: AssistantEntryPoint
      }
    }
  | {
      event: 'assistant__question_asked'
      properties: { surface: AssistantSurface; source: AssistantQuestionSource }
    }
  | {
      event: 'assistant__change_decided'
      properties: {
        surface: AssistantSurface
        action: string
        decision: 'approved' | 'cancelled'
      }
    }
  | { event: 'assistant__cleared'; properties: { surface: AssistantSurface } }
  | {
      /** Review on the nudge: the briefing card opened (no model call). */
      event: 'assistant__briefing_opened'
      properties: { action_type: string }
    }

export const trackAssistantEvent = ({ event, properties }: AssistantEvent) => {
  if (!sessionStorageService.getItem(SessionKey.ATTORNEY_EMAIL_KEY)) return
  segmentService.track(event, properties)
}
